import { Children, type ReactElement, type ReactNode } from 'react';
import { useInView } from '@/shared/hooks';
import { Stack } from '@/ui/layout/Stack';
import { Box, Skeleton, type SxProps, type Theme } from '@/ui/primitives';

type DeferredRenderBlockProps = {
  children: ReactNode;
  fallback?: ReactNode;
  hideFallbackVisuals?: boolean;
  minHeight?: string;
  rootMargin?: string;
  sx?: SxProps<Theme>;
};

export const DeferredRenderBlock = ({
  children,
  fallback,
  hideFallbackVisuals = true,
  minHeight = '20rem',
  rootMargin = '300px 0px',
  sx,
}: DeferredRenderBlockProps): ReactElement => {
  const { ref, inView } = useInView<HTMLDivElement>({
    rootMargin,
    triggerOnce: true,
  });
  const baseSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    ...(inView ? {} : { minHeight }),
  };
  const resolvedSx: SxProps<Theme> = sx
    ? [baseSx, ...(Array.isArray(sx) ? [...sx] : [sx])]
    : baseSx;
  const resolvedFallback = fallback ?? (
    <Stack gap="md" sx={{ width: '100%', minWidth: 0, minHeight, py: 'sm' }}>
      <Skeleton variant="rounded" width="28%" height={24} />
      <Skeleton variant="rounded" width="100%" height={160} />
      <Skeleton variant="rounded" width="82%" height={20} />
    </Stack>
  );

  return (
    <Box ref={ref} sx={resolvedSx}>
      {inView ? (
        Children.toArray(children)
      ) : (
        <Box
          aria-hidden={hideFallbackVisuals ? 'true' : undefined}
          sx={hideFallbackVisuals ? { visibility: 'hidden' } : undefined}
        >
          {Children.toArray(resolvedFallback)}
        </Box>
      )}
    </Box>
  );
};
